import angular from 'angular'
import { ApiProduct } from '@vend/types'

import { $q } from '../ng-compat/imports'
import { EventEmitter } from '../util/event-emitter'
import {
  QuickKeyNodeType,
  DEFAULT_FOLDER,
  DEFAULT_PAGE,
  DEFAULT_QUICK_KEY,
} from './qk.constants'
import {
  buildQuickKeysLayout,
  findFolderInUpdatedLayout,
  findQuickKeyById,
  getProductDetails,
  isPlaceholderPage,
  updateLocks,
} from './qk-layout'
import { navigate } from './qk-navigation'
import {
  ApiNode,
  QuickKey,
  QuickKeyNonRoot,
  QuickKeyPage,
  QuickKeyPageChild,
  QuickKeyRoot,
} from './qk-types'

type Events = {
  layoutUpdate: [$TSFixMe]
  layoutUpdatingChange: [$TSFixMe]
  layoutUpdateError: [$TSFixMe]
  'quick-key-selected': [$TSFixMe]
  'quick-keys-locked': [$TSFixMe]
  'quick-keys-unlocked': [$TSFixMe]
}

interface UpdateOperation {
  performUpdate?: (value?: $TSFixMe) => $TSFixMe
  affectedQuickKeys?: unknown[]
  finally?: () => void
}

/**
 * Model for a quick keys layout.
 *
 * @module vendQuickKeys.QuickKeysLayout
 */
angular
  .module('vendQuickKeys')
  .factory('QuickKeysLayout', QuickKeysLayoutFactory)

function QuickKeysLayoutFactory(qkNodesResource: $TSFixMe) {
  /**
   * Model representing a quick keys layout.
   *
   * @class QuickKeysLayout
   * @extends EventEmitter
   * @constructor
   *
   * @param  layoutData {Object}
   *         The data for the quick keys layout as returned by the /api/2.0/button_layouts API
   *
   * @param  [options] {Object}
   *
   *         @param [options.editable=false] {Boolean}
   *                Whether the layout is currently editable
   */
  class QuickKeysLayout extends EventEmitter<Events> {
    private _draggedQuickKey: $TSFixMe
    private _editTarget: $TSFixMe
    private _editable: $TSFixMe
    private _folder: $TSFixMe
    private _layoutData: $TSFixMe
    private _layoutLocked: $TSFixMe
    private _lockedQuickKeys: $TSFixMe
    private _queue: $TSFixMe
    private _queuedUpdates: $TSFixMe
    private _quickKeysLayoutDataAdaptor: $TSFixMe
    private _rootNode: $TSFixMe
    private _updateInProgress: $TSFixMe

    constructor(layoutData: $TSFixMe, options: $TSFixMe = {}) {
      super()

      this._queue = $q.resolve()
      this._lockedQuickKeys = {}

      this._layoutData = layoutData
      this._editable = Boolean(options.editable)
      this._layoutLocked = false

      this._rootNode = null
      this._folder = null
      this._editTarget = null

      this._draggedQuickKey = null

      this._updateInProgress = false
      this._queuedUpdates = 0

      this.updateLayout()
    }

    // ============================== Public API ============================== //

    /**
     * The root node of the quick keys layout.
     *
     * @property rootNode
     * @type {Object}
     * @readonly
     */
    public get rootNode() {
      return this._rootNode
    }

    /**
     * The quick key node representing the current open folder (or null if there is no open folder).
     *
     * @property folder
     * @type {Object}
     */
    public get folder() {
      return this._folder
    }

    public set folder(folderNode) {
      this._folder = folderNode

      if (this._editable) {
        this._resetEditTarget()
      }
    }

    /**
     * Whether the layout is currently editable.
     *
     * @property editable
     * @type {Boolean}
     */
    public get editable() {
      return !this._layoutLocked && this._editable
    }

    public set editable(editable) {
      const before = this._editable
      this._editable = Boolean(editable)

      if (before !== this._editable) {
        this.updateLayout()
      }
    }

    /**
     * Whether a layout update is currently in progress.
     *
     * @property updateInProgress
     * @return {Boolean}
     */
    public get updateInProgress() {
      return this._updateInProgress
    }

    /**
     * The blank quick key node in the position where the next new quick key will be added.
     *
     * @property editTarget
     * @type {Object}
     */
    public get editTarget() {
      return this._editTarget
    }

    public set editTarget(editTarget) {
      this._editTarget = editTarget
    }

    /**
     * The quick key that is currently being dragged (as part of a drag and drop event).
     *
     * @property
     * @type {Object}
     */
    public get draggedQuickKey() {
      return this._draggedQuickKey
    }

    public set draggedQuickKey(quickKey) {
      this._draggedQuickKey = quickKey
    }

    /**
     * Handle a quick key being selected in the layout
     * Will close any open folders if the close folder option is set to true
     *
     * @method selectQuickKey
     *
     * @param {Object} quickKey
     *        An object representing the selected quick key
     */
    public selectQuickKey(quickKey: QuickKey) {
      const qkProductDetails = getProductDetails(quickKey)
      if (this.getCloseFolderOption()) {
        this._folder = null
      }

      this.emit('quick-key-selected', qkProductDetails)
    }

    /**
     * Update the quick keys layout if conditions have changed or there is updated layout data available.
     *
     * @method updateLayout
     *
     * @param  {Object} [updatedLayoutData]
     *         The updated data for the quick keys layout as returned by the /api/2.0/button_layouts API
     */
    public updateLayout(updatedLayoutData?: $TSFixMe) {
      if (!angular.isUndefined(updatedLayoutData)) {
        this._layoutData = updatedLayoutData
      }

      const originalLayoutId = this._rootNode && this._rootNode.node.id

      if (this._layoutData && this._layoutData.nodes) {
        this._rootNode = buildQuickKeysLayout(this._layoutData, {
          padEmptyRows: this._editable,
          lockedQuickKeys: Object.keys(this._lockedQuickKeys),
        })

        if (this._folder) {
          this._folder = findFolderInUpdatedLayout(
            this._folder,
            originalLayoutId,
            this._rootNode
          )
        }

        if (this._editable) {
          this._resetEditTarget()
        }
      } else {
        this._rootNode = null
        this._folder = null
      }
    }

    /**
     * Add a new quick key with the given product.
     *
     * @method addQuickKey
     *
     * @param  {Product} product
     *         The product to add a quick key for
     *
     * @param  {Object} [options]
     *
     *         @param {Boolean} [options.exactMatch=false]
     *                Whether the product being added is an exact match for the current variant or whether it should be
     *                added as a generic variant parent (which will trigger the variant picker). Not applicable for
     *                products that do not have variants.
     *
     * @return {Promise<Object>} A promise resolved with the updated layout data once the quick key has been added or
     *                           rejected if an error was encountered in the process
     */
    public addQuickKey(
      product: ApiProduct,
      options: {
        exactMatch?: boolean
      } = {}
    ) {
      return $q((resolve, reject) => {
        if (!this.editable || !this._editTarget) {
          return reject(new Error('This layout is not currently editable'))
        }

        if (!product || !product.id) {
          return reject(
            new TypeError(
              'The given product is not defined or has no product ID'
            )
          )
        }

        const { exactMatch } = options
        const { node, parentNode } = this._editTarget

        const quickKeyNode = this._buildQuickKeyNode({
          quickKeyData: {
            product_id: product.id,
            position: node.position,
            parent_node_id: node.parent_node_id,
          },
          attributeData: {
            label: exactMatch ? product.variant_name : product.name,
            isChild: exactMatch ? 1 : 0,
          },
          nodeDefaults: DEFAULT_QUICK_KEY,
        })

        const operations = [
          this._operationEnsurePageExists(parentNode),
          this._operationCreateQuickKey(quickKeyNode),
        ]

        this._queueUpdate({
          operations,
          affectedQuickKeys: [this._editTarget],
        }).then(resolve, reject)
      })
    }

    /**
     * Get the current close folder option
     *
     * @method getCloseFolderOption
     *
     * @return {Boolean} A boolean representing the current close folder option
     */
    public getCloseFolderOption() {
      const firstPage = this._rootNode.children[0]

      return firstPage.viewData.close_folder
    }

    /**
     * Set the close folder option, will default to false if no options if provided
     *
     * @method setCloseFolderOption
     *
     * @param {Boolean} closeFolder
     *
     * @return {Promise<Object>} A promise resolved with the updated layout data containing the close folder option
     */
    public setCloseFolderOption(closeFolder: boolean = false) {
      const firstPage = this._rootNode.children[0]

      const operations: UpdateOperation[] = [
        this._operationEnsurePageExists(firstPage.node),
        this._operationSetCloseFolderOption(closeFolder),
      ]

      return this._queueUpdate({ operations })
    }

    /**
     * Process a drag and drop event. Does different things depending on the types of the origin and destination quick
     * keys.
     *
     * @method processDragAndDrop
     *
     * @param  {Object} params
     *
     *         @param  {QuickKey} params.quickKeyOrigin
     *                 The quick key that was dragged
     *
     *         @param  {QuickKey} params.quickKeyDestination
     *                 The quick key that the origin quick key was dropped on
     *
     * @return {Promise<Object>} A promise resolved with the updated layout data once the appropriate action has been
     *                           completed
     */
    public processDragAndDrop({
      quickKeyOrigin,
      quickKeyDestination,
    }: {
      quickKeyOrigin: QuickKey
      quickKeyDestination: QuickKey
    }) {
      const { QUICK_KEY } = QuickKeyNodeType

      if (
        quickKeyOrigin.type === QUICK_KEY &&
        quickKeyDestination.type === QUICK_KEY
      ) {
        return this.mergeQuickKeysIntoFolder({
          quickKeyOrigin,
          quickKeyDestination,
        })
      } else {
        return this.moveQuickKey({ quickKeyOrigin, quickKeyDestination })
      }
    }

    /**
     * Move an existing quick key to a new position.
     *
     * @method moveQuickKey
     *
     * @param  {Object} params
     *
     *         @param  quickKeyOrigin {Object}
     *                 The quick key which is to be moved
     *
     *         @param  quickKeyDestination {Object}
     *                 The destination quick key
     *
     * @return {Promise<Object>} A promise resolved with the updated layout data once the quick key has been moved.
     */
    public moveQuickKey({ quickKeyOrigin, quickKeyDestination }: $TSFixMe) {
      const affectedQuickKeys = []

      if (quickKeyDestination.type === QuickKeyNodeType.FOLDER) {
        // Lock the folder node and change the destination to the first blank therein
        affectedQuickKeys.push(quickKeyDestination)
        quickKeyDestination = this._findFirstBlankNode(quickKeyDestination)
      }

      if (!quickKeyDestination) {
        return $q.reject(
          new Error('The destination to move that quick key to is invalid')
        )
      }

      const operations: UpdateOperation[] = [
        this._operationMoveQuickKey({ quickKeyOrigin, quickKeyDestination }),
      ]

      // If we're moving to a different page...
      if (
        quickKeyDestination.node.parent_node_id !==
        quickKeyOrigin.node.parent_node_id
      ) {
        // 1) Before moving the quick key make sure the destination page exists
        operations.unshift(
          this._operationEnsurePageExists(quickKeyDestination.parentNode)
        )
        // 2) After moving the quick key, check whether the old page is empty and can be deleted
        operations.push(
          this._operationDeleteContainingPageIfEmpty(quickKeyOrigin)
        )
      }
      return this._queueUpdate({ operations, affectedQuickKeys })
    }

    /**
     * Merge the two given quick keys into a folder. The folder is created at the location of the destination quick key
     * and the destination and origin quick keys are placed 1st and 2nd in that folder respectively.
     *
     * @method mergeQuickKeysIntoFolder
     *
     * @param  {Object} params
     *
     *         @param  {QuickKey} params.quickKeyOrigin
     *                 The quick key that was dragged - will be placed in the folder at index 1
     *
     *         @param  {QuickKey} params.quickKeyDestination
     *                 The quick key that the origin quick key was dropped on - the folder will be created at this
     *                 position and this quick key will be placed in the folder at index 0
     *
     * @return {Promise<Object>} A promise resolved with the updated layout data once the folder has been created and
     *                           the two quick keys moved inside it
     */
    public mergeQuickKeysIntoFolder({
      quickKeyOrigin,
      quickKeyDestination,
    }: {
      quickKeyOrigin: QuickKeyNonRoot
      quickKeyDestination: QuickKeyPageChild
    }) {
      const { node, viewData } = quickKeyDestination

      const folderNode = this._buildQuickKeyNode({
        quickKeyData: {
          product_id: null,
          position: node.position,
          parent_node_id: node.parent_node_id,
        },
        attributeData: {
          label: 'New Folder',
          color: viewData.color,
        },
        nodeDefaults: DEFAULT_FOLDER,
      })

      const folderPageNode = this._buildQuickKeyNode({
        quickKeyData: { position: 0 },
        nodeDefaults: DEFAULT_PAGE,
      })

      const firstQuickKeyNode = angular.merge({}, quickKeyDestination.node, {
        position: 0,
      })
      // Remove the ID as we'll be recreating this node and the server will generate a new ID
      delete firstQuickKeyNode.id

      const secondQuickKey = angular.merge({}, quickKeyDestination, {
        node: { position: 1 },
      })

      const mergeQuickKeysOperation = {
        affectedQuickKeys: [quickKeyOrigin, quickKeyDestination],
        performUpdate: () => {
          return this._deleteQuickKey(quickKeyDestination)
            .then(() => this._createQuickKey(folderNode))
            .then((createdFolderNode: $TSFixMe) =>
              this._createQuickKey(folderPageNode, createdFolderNode)
            )
            .then((createdPageNode: $TSFixMe) => {
              return (
                this._createQuickKey(firstQuickKeyNode, createdPageNode)
                  // Resolve to the created page so the following _moveQuickKey step can set the parent node of the
                  // moved origin quick key
                  .then(() => createdPageNode)
              )
            })
            .then((parentNode: $TSFixMe) => {
              return this._moveQuickKey({
                quickKeyOrigin,
                quickKeyDestination: secondQuickKey,
                parentNode,
              })
            })
        },
      }

      const operations: UpdateOperation[] = [
        mergeQuickKeysOperation,
        this._operationDeleteContainingPageIfEmpty(quickKeyOrigin),
      ]

      return this._queueUpdate({ operations })
    }

    /**
     * Update the given quick key that has been edited. The `viewData` property should contain the updated properties
     * (color, label, etc) - these will be copied across to the `attributes` property and sent to the server.
     *
     * @method updateQuickKey
     *
     * @param  {QuickKey} quickKey
     *         The quick key to update
     *
     * @return {Promise<QuickKey>} A promise resolved with the updated layout data once the quick key has been updated
     *                             or rejected if something goes wrong in the process
     */
    public updateQuickKey(quickKey: QuickKeyNonRoot) {
      return this._queueUpdate({
        operations: [this._operationUpdateQuickKey(quickKey)],
      })
    }

    /**
     * Delete the given quick key from the current layout.
     *
     * @method deleteQuickKey
     *
     * @param  {QuickKey} quickKey
     *         The quick key to delete
     *
     * @return {Promise} A promise resolved with the updated layout data once the quick key has been deleted
     *                   or rejected if something goes wrong in the process
     */
    public deleteQuickKey(quickKey: QuickKeyNonRoot) {
      const operations = [
        this._operationDeleteQuickKey(quickKey),
        this._operationDeleteContainingPageIfEmpty(quickKey),
      ]
      return this._queueUpdate({ operations })
    }

    /**
     * Responsbile for determining if the provided quick key is a valid droppable destination for the currently
     * dragged quick key.
     *
     * @method isValidDropDestination
     *
     * @param  {QuickKey} quickKey
     *         The quick key destination to evaluate
     *
     * @return {Boolean} True if the provided quick key is a valid destination, false otherwise.
     */
    public isValidDropDestination(quickKey: QuickKey) {
      if (!this._draggedQuickKey || quickKey.locked) {
        return false
      }

      const { FOLDER, QUICK_KEY, BLANK } = QuickKeyNodeType
      const draggedQkIsFolder = this._draggedQuickKey.type === FOLDER

      switch (quickKey.type) {
        case FOLDER:
          // Only allow dragging into a folder if we're not dragging another folder
          return !draggedQkIsFolder

        case QUICK_KEY: {
          const isInFolder = Boolean(quickKey.parentNode.parent_node_id)
          // Only allow dragging onto another quick key if we're not dragging a folder and not inside a folder
          return !(draggedQkIsFolder || isInFolder)
        }

        case BLANK:
          return true

        default:
          return false
      }
    }

    /**
     * Retrieves information for a product associated with a quick key. Employs the getProduct method attached to the
     * _quickKeysLayoutDataAdaptor provided by the consuming application.
     *
     * @method getProduct
     *
     * @param  {QuickKey} quickKey
     *         The quick key to get the related product for
     *
     * @return {Promise} A promise resolved with the acquired product if a layoutDataAdaptor instance and getProduct
     *                   method have been provided, undefined otherwise.
     */
    public getProduct(quickKey: QuickKey) {
      const layoutDataAdaptor = this._quickKeysLayoutDataAdaptor
      return $q.resolve(
        layoutDataAdaptor &&
          layoutDataAdaptor.getProduct(quickKey.node.product_id)
      )
    }

    /**
     * Responsbile for registering a quick keys layout data adaptor instance. Allows access to methods provided by the
     * consuming application such as obtaining product information related to a quick key.
     *
     * @method registerDataAdaptor
     *
     * @param  {QuickKeysLayoutDataAdaptor} layoutDataAdaptor
     *         The quick key layout data adaptor instance
     */
    public registerDataAdaptor(layoutDataAdaptor: $TSFixMe) {
      this._quickKeysLayoutDataAdaptor = layoutDataAdaptor
    }

    // ============================== Private API ============================== //

    /**
     * Find the first blank node in the given root node.
     *
     * @private
     * @method _findFirstBlankNode
     *
     * @param  {Node} rootNode
     *         The root node to find the first blank node within
     *
     * @return {Node} The first available blank node inside the tree of quick keys
     */
    private _findFirstBlankNode(rootNode: QuickKeyRoot) {
      const navData = { fromPosition: { pageIndex: 0, position: -1 } }
      const options = { includeBlanks: true, excludeNonBlanks: true }
      const navigationData = navigate(rootNode, navData, options)
      return navigationData.to
    }

    /**
     * Reset the "edit target" (the blank node in the position where the next new quick key will be added) to the first
     * available blank node in the grid (or the current open folder).
     *
     * @private
     * @method _resetEditTarget
     */
    private _resetEditTarget() {
      const rootNode = this._folder || this._rootNode
      this._editTarget = this._findFirstBlankNode(rootNode)
    }

    /**
     * Queue an update operation. This ensures we only perform one update at a time and if any of the updates fail we
     * abandon all queued updates.
     *
     * @private
     * @method _queueUpdate
     *
     * @param  {Object} params
     *
     *         @param  {Array<UpdateOperation>} params.operations
     *                 The array of operations to perform as a single update. Each operation is executed in series.
     *
     *                 @param {Function} operation.performUpdate
     *                        The function that will be invoked to perform the update
     *
     *                  @param {Array<QuickKey>} [operation.affectedQuickKeys]
     *                         Array of quick keys that'll be affected by the update (to ensure they are locked)
     *
     *                  @param {Function} [operation.finally]
     *                         An optional additional function that will be invoked after the update has completed
     *                         regardless of success or failure.
     *
     *         @param {Array<QuickKey>} [params.affectedQuickKeys]
     *                Array of quick keys that'll be affected by the update (to ensure they are locked)
     *
     * @return {Promise} A promise resolved with the result of the update operation once it has been performed
     */
    private _queueUpdate({
      operations,
      affectedQuickKeys = [],
    }: {
      operations: UpdateOperation[]
      affectedQuickKeys?: QuickKey[]
    }) {
      const performUpdateFns: $TSFixMe[] = []
      const finallyFns: $TSFixMe[] = []
      operations.forEach(operation => {
        if (operation.affectedQuickKeys) {
          Array.prototype.push.apply(
            affectedQuickKeys,
            operation.affectedQuickKeys
          )
        }
        if (operation.performUpdate) {
          performUpdateFns.push(operation.performUpdate)
        }
        if (operation.finally) {
          finallyFns.push(operation.finally)
        }
      })
      const alreadyLocked = affectedQuickKeys.some(
        (quickKey: $TSFixMe) => quickKey.locked
      )
      if (alreadyLocked) {
        return $q.reject(
          new Error(
            'Cannot perform operation as one or more of the affected quick keys is locked.'
          )
        )
      }

      this._lockQuickKeys(affectedQuickKeys)

      if (this._editTarget && this._editTarget.locked) {
        // Reset the edit target if we just locked it
        this._resetEditTarget()
      }

      this._updateQueuedCount(true)

      const performUpdates = () => {
        let promise = $q.resolve()
        performUpdateFns.forEach(performUpdate => {
          promise = promise.then(result => performUpdate(result))
        })
        return promise
      }

      this._queue = this._queue
        .then(() => {
          return performUpdates()
            .then(
              () => this._handleLayoutUpdate(),
              error => this._handleUpdateError(error)
            )
            .finally(() => finallyFns.forEach(finallyFn => finallyFn()))
        })
        .finally(() => {
          this._updateQueuedCount(false)
          this._unlockQuickKeys(affectedQuickKeys)
        })

      return this._queue
    }

    /**
     * Responsible for incrementing and decrementing the number of 'update' requests in progress.
     *
     * @private
     * @method _updateQueuedCount
     *
     * @param  {Boolean} isIncrement
     *         True if the total of queued counts should be incremented, decrementing otherwise
     */
    private _updateQueuedCount(isIncrement: boolean) {
      if (isIncrement) {
        this._queuedUpdates += 1

        if (!this.updateInProgress) {
          this._setUpdateInProgress(true)
        }
      } else {
        this._queuedUpdates -= 1

        if (!this._queuedUpdates) {
          this._setUpdateInProgress(false)
        }
      }
    }

    /**
     * Lock the given quick keys to prevent any subsequent modification of them until current updates are complete.
     *
     * @private
     * @method _lockQuickKeys
     *
     * @param  {Array<QuickKey>} quickKeys
     *         The quick keys to lock
     */
    private _lockQuickKeys(quickKeys: QuickKey[]) {
      quickKeys.forEach(quickKey => {
        this._lockedQuickKeys[quickKey.node.id] = true
      })
      updateLocks(this._rootNode, Object.keys(this._lockedQuickKeys))
      this.emit('quick-keys-locked', { quickKeys })
    }

    /**
     * Unlock the given quick keys.
     *
     * @private
     * @method _unlockQuickKeys
     *
     * @param  {Array<QuickKeys>} quickKeys
     *         The quick keys to unlock
     */
    private _unlockQuickKeys(quickKeys: QuickKey[]) {
      quickKeys.forEach(
        quickKey => delete this._lockedQuickKeys[quickKey.node.id]
      )
      updateLocks(this._rootNode, Object.keys(this._lockedQuickKeys))
      this.emit('quick-keys-unlocked', { quickKeys })
    }

    /**
     * Handle the underlying quick key layout data having been changed due to an edit action (add/edit/remove/move).
     * Updates the layout model and emits a `layoutUpdate` event.
     *
     * @private
     * @method _handleLayoutUpdate
     *
     * @return {Object} the updated layoutData
     */
    private _handleLayoutUpdate() {
      this.updateLayout()
      this.emit('layoutUpdate', {
        layoutData: this._layoutData,
      })
      return this._layoutData
    }

    /**
     * Handle an error that occurs during an edit action (add/edit/remove/move). Emits a `layoutUpdateError` event.
     *
     * @private
     * @method _handleUpdateError
     *
     * @param  {Error} error
     *         The error that caused the update to fail
     *
     * @return {Promise} A promise rejected with the given error
     */
    private _handleUpdateError(error: Error) {
      this.emit('layoutUpdateError', { error })
      return $q.reject(error)
    }

    /**
     * Responsible for communicating when a layout update is in progress, or when an update completes
     *
     * @private
     * @method _setUpdateInProgress
     *
     * @param {Boolean} isUpdating
     *        True if an update is in progress, false otherwise
     */
    private _setUpdateInProgress(isUpdating: boolean) {
      this._updateInProgress = isUpdating
      this.emit('layoutUpdatingChange', {
        layoutUpdating: this._updateInProgress,
      })
    }

    /**
     * Return an UpdateOperation for ensuring a given page node exists in the API. The operation does nothing if the
     * page is a legitimate page node but creates a new page node if the page node is just a placeholder.
     *
     * @private
     * @method _operationEnsurePageExists
     *
     * @param  {Node} pageNode
     *         The page node to ensure exists in the API
     *
     * @return {UpdateOperation} The update operation to ensure the page exists - resolves to the existing/created
     *                           page node
     */
    private _operationEnsurePageExists(pageNode: ApiNode): UpdateOperation {
      if (!isPlaceholderPage(pageNode)) {
        return { performUpdate: () => pageNode }
      }

      // Lock the entire layout while we create the page to add the quick key to
      this._layoutLocked = true

      return {
        performUpdate: () => {
          const pageNodeData = this._buildQuickKeyNode({
            quickKeyData: {
              position: pageNode.position,
              parent_node_id: pageNode.parent_node_id,
            },
            nodeDefaults: DEFAULT_PAGE,
          })

          return qkNodesResource
            .createQuickKey(this._layoutData.id, pageNodeData)
            .then((createdPageNode: $TSFixMe) =>
              this._addNodeToLayoutData(createdPageNode)
            )
        },
        finally: () => {
          this._layoutLocked = false
        },
      }
    }

    /**
     * Return an UpdateOperation for deleting the containing page of the given quick key if moving/deleting said quick
     * key leaves the page empty (and the page is the last page (and not the only page) in the current layer).
     *
     * @private
     * @method _operationDeleteContainingPageIfEmpty
     *
     * @param  {QuickKey} quickKey
     *         The quick key to potentially clean up its page node after being moved/removed
     *
     * @return {UpdateOperation} The update operation to delete the page if necessary
     */
    private _operationDeleteContainingPageIfEmpty(
      quickKey: QuickKeyNonRoot
    ): UpdateOperation {
      const pageNode = quickKey.parentNode
      if (isPlaceholderPage(pageNode)) {
        // If the page is a placeholder and is no longer necessary it'll get removed when the layout is next rebuilt
        return {}
      }

      const pageQuickKey = findQuickKeyById(
        this.rootNode,
        pageNode.id
      ) as QuickKeyPage
      if (
        !pageQuickKey ||
        pageQuickKey.children.length > 1 ||
        pageQuickKey.children[0].node.id !== quickKey.node.id
      ) {
        // Couldn't find the page or the given quick key is not the last quick key on the page
        return {}
      }

      const { pageIndex, numberOfPages } = pageQuickKey.viewData
      if (pageIndex === 0 || pageIndex !== numberOfPages - 1) {
        // Don't delete the page if it is not the last page or it is the first (and only) page in this layer
        return {}
      }

      // Lock the entire layout while we delete the page to ensure we don't queue up any more adds/etc onto this page
      this._layoutLocked = true

      return {
        performUpdate: () => {
          // Double check that there are no nodes referencing this page anymore
          if (
            this._layoutData.nodes.every(
              (node: $TSFixMe) => node.parent_node_id !== pageNode.id
            )
          ) {
            return this._deleteQuickKey(pageQuickKey)
          }
        },
        finally: () => {
          this._layoutLocked = false
        },
      }
    }

    /**
     * Return an UpdateOperation for creating a new quick key from the given quick key node data.
     *
     * @private
     * @method _operationCreateQuickKey
     *
     * @param  {Node} quickKeyNode
     *         The quick key node data to create the quick key from
     *
     * @return {UpdateOperation} The update operation to create the quick key - resolves to the created quick key
     */
    private _operationCreateQuickKey(quickKeyNode: ApiNode) {
      return {
        performUpdate: (createdPageNode: $TSFixMe) =>
          this._createQuickKey(quickKeyNode, createdPageNode),
      }
    }

    /**
     * Return an UpdateOperation for moving a given quick key to a new destination.
     *
     * @private
     * @method _operationMoveQuickKey
     *
     * @param  {Object} params
     *
     *         @param {Object} quickKeyOrigin
     *                The quick key which is to be moved
     *
     *         @param {Object} quickKeyDestination
     *                The destination quick key
     *
     * @return {UpdateOperation} The update operation to move the given quick key - resolves to the updated quick key
     */
    private _operationMoveQuickKey({
      quickKeyOrigin,
      quickKeyDestination,
    }: {
      quickKeyOrigin: QuickKeyNonRoot
      quickKeyDestination: QuickKeyNonRoot
    }) {
      return {
        affectedQuickKeys: [quickKeyDestination, quickKeyOrigin],
        performUpdate: (parentNode: $TSFixMe) =>
          this._moveQuickKey({
            quickKeyOrigin,
            quickKeyDestination,
            parentNode,
          }),
      }
    }

    /**
     * Return an UpdateOperation for updating the attributes of a given quick key. The `viewData` property should
     * contain the updated properties (color, label, etc) - these will be copied across to the `attributes` property
     * and sent to the server.
     *
     * @private
     * @method _operationUpdateQuickKey
     *
     * @param  {QuickKey} quickKey
     *         The quick key to update
     *
     * @return {UpdateOperation} The update operation to update the given quick key - resolves to the updated quick key
     */
    private _operationUpdateQuickKey(quickKey: QuickKeyNonRoot) {
      quickKey.node.attributes.forEach((attr: $TSFixMe) => {
        const updatedValue = (quickKey.viewData as $TSFixMe)[attr.key]
        if (!angular.isUndefined(updatedValue)) {
          if (attr.key === 'showImage') {
            attr.value = updatedValue ? 1 : 0
          } else {
            attr.value = updatedValue
          }
        }
      })

      return {
        affectedQuickKeys: [quickKey],
        performUpdate: () => {
          return qkNodesResource
            .updateQuickKey(this._layoutData.id, quickKey.node)
            .then((updatedNode: $TSFixMe) =>
              this._replaceNodeInLayoutData(updatedNode)
            )
        },
      }
    }

    /**
     * Return the UpdateOperation for updating attributes of the root node. Will update the close_folder attribute
     *
     * @private
     *
     * @method _operationSetCloseFolderOption
     *
     * @param {Boolean} closeFolder
     *
     * @return {UpdateOperation} The update operation to update the given quick key - resolves to the updated quick key
     */
    private _operationSetCloseFolderOption(closeFolder: boolean) {
      return {
        affectedQuickKeys: [],
        performUpdate: (createdPageNode: $TSFixMe) => {
          let closeFolderAttr = createdPageNode.attributes.find(
            (attr: $TSFixMe) => attr.key === 'close_folder'
          )

          if (!closeFolderAttr) {
            closeFolderAttr = { key: 'close_folder' }
            createdPageNode.attributes.push(closeFolderAttr)
          }
          closeFolderAttr.value = closeFolder ? '1' : '0'

          return qkNodesResource
            .updateQuickKey(this._layoutData.id, createdPageNode)
            .then((updatedNode: $TSFixMe) =>
              this._replaceNodeInLayoutData(updatedNode)
            )
        },
      }
    }

    /**
     * Return an UpdateOperation for deleting a given quick key.
     *
     * @private
     * @method _operationDeleteQuickKey
     *
     * @param  {QuickKey} quickKey
     *         The quick key to delete
     *
     * @return {UpdateOperation} The update operation to delete the given quick key - resolves to the deleted quick key
     */
    private _operationDeleteQuickKey(quickKey: QuickKey) {
      return {
        affectedQuickKeys: [quickKey],
        performUpdate: () => this._deleteQuickKey(quickKey),
      }
    }

    /**
     * Create a new quick key from the given quick key node data optionally updating the parent_node_id to the ID of the
     * given parent node.
     *
     * @private
     * @method _createQuickKey
     *
     * @param  {Node} quickKeyNode
     *         The quick key node data to create the quick key from
     *
     * @param  {Node} parentNode
     *         The parent node of the quick key node to create - to ensure the correct parent_node_id is used
     *
     * @return {Promise<Node>} A promise resolved with the created quick key node once it has been created
     */
    private _createQuickKey(quickKeyNode: ApiNode, parentNode?: ApiNode) {
      if (parentNode) {
        quickKeyNode.parent_node_id = parentNode.id
      }

      return qkNodesResource
        .createQuickKey(this._layoutData.id, quickKeyNode)
        .then((createdQuickKeyNode: $TSFixMe) =>
          this._addNodeToLayoutData(createdQuickKeyNode)
        )
    }

    /**
     * Move the given origin quick key to a new destination.
     *
     * @private
     * @method _moveQuickKey
     *
     * @param  {Object} params
     *
     *         @param {Object} quickKeyOrigin
     *                The quick key which is to be moved
     *
     *         @param {Object} quickKeyDestination
     *                The destination quick key
     *
     *         @param {Node} [parentNode]
     *                The parent node of the destination quick key - to ensure the correct parent_node_id is used
     *
     * @return {Promise<Node>} A promise resolved with the updated quick key node once it has been moved
     */
    private _moveQuickKey({
      quickKeyOrigin,
      quickKeyDestination,
      parentNode,
    }: {
      quickKeyOrigin: QuickKeyNonRoot
      quickKeyDestination: QuickKeyNonRoot
      parentNode?: ApiNode
    }) {
      const parentNodeId =
        (parentNode && parentNode.id) || quickKeyDestination.node.parent_node_id

      const quickKeyData = {
        id: quickKeyOrigin.node.id,
        parent_node_id: parentNodeId,
        position: quickKeyDestination.node.position,
      }

      return qkNodesResource
        .updateQuickKey(this._layoutData.id, quickKeyData)
        .then((updatedNode: $TSFixMe) =>
          this._replaceNodeInLayoutData(updatedNode)
        )
    }

    /**
     * Delete the given quick key from the layout.
     *
     * @private
     * @method _deleteQuickKey
     *
     * @param  {QuickKey} quickKey
     *         The quick key to delete
     *
     * @return {Promise<Node>} A promise resolved with the deleted quick key node
     */
    private _deleteQuickKey(quickKey: QuickKey) {
      return qkNodesResource
        .deleteQuickKey(this._layoutData.id, quickKey.node.id)
        .then(() => this._removeNodeFromLayoutData(quickKey.node))
    }

    /**
     * Add the given node to the layout data.
     *
     * @private
     * @method _addNodeToLayoutData
     *
     * @param  {Node} quickKeyNode
     *         The quick key node to add to the layout data
     *
     * @return {Node} The newly added quick key node
     */
    private _addNodeToLayoutData(quickKeyNode: Node) {
      this._layoutData.nodes.push(quickKeyNode)
      return quickKeyNode
    }

    /**
     * Replace existing equivalent node in the layout data with the given node.
     *
     * @private
     * @method _replaceNodeInLayoutData
     *
     * @param  {Object} quickKeyNode
     *         The updated quick key node
     *
     * @return {Object} The updated quick key node
     */
    private _replaceNodeInLayoutData(quickKeyNode: ApiNode) {
      const nodeIndex = this._findNodeIndex(quickKeyNode)

      if (nodeIndex !== -1) {
        this._layoutData.nodes[nodeIndex] = quickKeyNode
      } else {
        throw new Error('Unable to find the quickKey within the layout')
      }

      return quickKeyNode
    }

    /**
     * Remove existing node from the layout.
     *
     * @method _removeNodeFromLayoutData
     *
     * @param  {Object} quickKeyNode
     *         The quick key node to remove
     *
     * @return {Object} The removed quick key node
     */
    private _removeNodeFromLayoutData(quickKeyNode: ApiNode) {
      const nodeIndex = this._findNodeIndex(quickKeyNode)

      if (nodeIndex !== -1) {
        this._layoutData.nodes.splice(nodeIndex, 1)
      } else {
        throw new Error('Unable to find the quickKey within the layout')
      }

      return quickKeyNode
    }

    /**
     * Locate a node in the layout data and return it's index position
     *
     * @method _findNodeIndex
     *
     * @param  {Object} quickKeyNode
     *         The quick key node to search for
     *
     * @return {Number} The index position of the node
     */
    private _findNodeIndex(quickKeyNode: ApiNode) {
      return this._layoutData.nodes.findIndex(
        (node: $TSFixMe) => node.id === quickKeyNode.id
      )
    }

    /**
     * Build a new node with the given data, attributes and defaults.
     *
     * @private
     * @method _buildQuickKeyNode
     *
     * @param  {Object} params
     *
     *         @param {Object} params.quickKeyData
     *                The base quick key data (product_id, parent_node_id, etc)
     *
     *         @param {Object} [params.attributeData]
     *                The attributes to set e.g. { isChild: 1, label: 'My product' }
     *
     *         @param {Object} [params.nodeDefaults]
     *                The default attribute values for the type of node being created
     *
     * @return the created quick key node
     */
    private _buildQuickKeyNode({
      quickKeyData,
      attributeData,
      nodeDefaults,
    }: {
      quickKeyData: $TSFixMe
      attributeData?: $TSFixMe
      nodeDefaults?: $TSFixMe
    }) {
      const node = angular.merge({ attributes: [] }, quickKeyData)

      const attributes = angular.merge({}, nodeDefaults, attributeData || {})
      angular.forEach(attributes, (value, key) =>
        node.attributes.push({ key, value })
      )

      return node
    }
  }

  return QuickKeysLayout
}
